import { BitkitSortableColumnHeaderProps } from '@bitrise/bitkit-v2';
import { AriaAttributes, useEffect, useState } from 'react';

import useQueryParams from '@/hooks/useQueryParams';
import { mergeQueryParams } from '@/utils/queryParams';

type SortDirection = BitkitSortableColumnHeaderProps['direction'];

export const toSortDirection = (sortedBy: AriaAttributes['aria-sort']): SortDirection =>
  sortedBy === 'ascending' || sortedBy === 'descending' ? sortedBy : 'none';

/**
 * The asc -> desc -> none cycle. `BitkitTable.SortableColumnHeader` only reports clicks, so every
 * sortable table has to advance the direction itself.
 */
export const nextSortDirection = (sortedBy: AriaAttributes['aria-sort']): SortDirection => {
  switch (toSortDirection(sortedBy)) {
    case 'ascending':
      return 'descending';
    case 'descending':
      return 'none';
    default:
      return 'ascending';
  }
};

interface UseTableSortState<T extends string> {
  sortBy?: T;
  sortDirection: AriaAttributes['aria-sort'];
}

interface UseTableSortParams<T extends string> {
  defaultSortBy?: T;
  defaultSortDirection?: AriaAttributes['aria-sort'];
  historyParams?: { sortBy: string; sortDirection: string };
  onChange?: (newState: UseTableSortState<T>) => void;
}

interface UseTableSortResult<T extends string> extends UseTableSortState<T> {
  /** The raw `aria-sort` pair, for call sites that own the cycle themselves; a v2 header takes `registerSortableColumn`. */
  registerColumn: (name: T) => {
    onSortClick: (sortDirection?: AriaAttributes['aria-sort']) => void;
    sortedBy: NonNullable<AriaAttributes['aria-sort']>;
  };
  registerSortableColumn: (name: T) => Pick<BitkitSortableColumnHeaderProps, 'direction' | 'onSort'>;
  onSortClear: () => void;
}

export const directionalSortByField = <T extends object, K extends keyof T>(
  a: T,
  b: T,
  sortBy: K,
  sortDirection?: string,
) => {
  const aValue = a[sortBy];
  const bValue = b[sortBy];
  let comparison = 0;

  if (aValue < bValue) {
    comparison = -1;
  }

  if (aValue > bValue) {
    comparison = 1;
  }

  return sortDirection === 'descending' ? comparison * -1 : comparison;
};

const useTableSort = <T extends string>({
  onChange,
  defaultSortBy,
  defaultSortDirection = 'none',
  historyParams,
}: UseTableSortParams<T>): UseTableSortResult<T> => {
  const queryParams = useQueryParams();

  const historySortBy = (historyParams && (queryParams.get(historyParams.sortBy) as T)) ?? undefined;
  const historySortDirection =
    (historyParams && (queryParams.get(historyParams.sortDirection) as AriaAttributes['aria-sort'])) ?? undefined;

  const [state, setState] = useState<UseTableSortState<T>>(() => {
    const defaultState: UseTableSortState<T> = {
      sortBy: defaultSortBy,
      sortDirection: defaultSortDirection,
    };

    if (historySortBy) {
      defaultState.sortBy = historySortBy;
    }
    if (historySortDirection) {
      defaultState.sortDirection = historySortDirection;
    }

    return defaultState;
  });

  useEffect(() => {
    setState((currentState) => ({
      ...currentState,
      sortBy: historySortBy ?? defaultSortBy,
      sortDirection: historySortDirection ?? defaultSortDirection,
    }));
  }, [defaultSortBy, defaultSortDirection, historySortBy, historySortDirection]);

  useEffect(() => {
    setState((currentState) => ({
      ...currentState,
      sortBy: historySortBy ?? defaultSortBy,
    }));
  }, [defaultSortBy, historySortBy]);

  const handleClick = (sortBy: T, sortDirection: AriaAttributes['aria-sort']) => {
    if (sortDirection !== 'ascending' && sortDirection !== 'descending') {
      return handleClear();
    }

    const newState = { ...state };

    newState.sortBy = sortBy;
    newState.sortDirection = sortDirection;

    setState(newState);
    onChange?.(newState);

    if (historyParams) {
      mergeQueryParams({
        set: [
          {
            type: historyParams.sortBy,
            value: newState.sortBy,
          },
          {
            type: historyParams.sortDirection,
            value: newState.sortDirection,
          },
        ],
      });
    }
  };

  const handleClear = () => {
    const newState = { ...state };

    newState.sortBy = undefined;
    newState.sortDirection = 'none';

    setState(newState);
    onChange?.(newState);

    if (historyParams) {
      mergeQueryParams({
        unset: [historyParams.sortBy, historyParams.sortDirection],
      });
    }
  };

  const directionOf = (sortBy: T) => (state.sortBy === sortBy ? toSortDirection(state.sortDirection) : 'none');

  return {
    ...state,
    registerColumn: (sortBy: T) => ({
      onSortClick: (sortDirection: AriaAttributes['aria-sort']) => handleClick(sortBy, sortDirection),
      sortedBy: state.sortBy === sortBy ? (state.sortDirection ?? 'none') : 'none',
    }),
    registerSortableColumn: (sortBy: T) => ({
      direction: directionOf(sortBy),
      onSort: () => handleClick(sortBy, nextSortDirection(directionOf(sortBy))),
    }),
    onSortClear: () => handleClear(),
  };
};

export default useTableSort;
